import { useState, useEffect } from 'react';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { ScrollText } from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

export default function ClientStatement() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await apiFetch(`${API_URL}/api/client-portal/statement`);
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-96" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="client-statement">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Statement of Account</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {data?.workspace_name && `${data.workspace_name} — `}
            {data?.contact?.name || 'Your account'}
          </p>
        </div>
        {data?.total_balance !== undefined && (
          <Card className={cn("px-4 py-2", data.total_balance > 0 ? "border-amber-300 dark:border-amber-700" : "border-emerald-300 dark:border-emerald-700")}>
            <p className="text-xs text-muted-foreground">Current Balance</p>
            <p className={cn("text-xl font-bold", data.total_balance > 0 ? "text-amber-600" : "text-emerald-600")} data-testid="statement-balance">
              {formatCurrency(data.total_balance)}
            </p>
          </Card>
        )}
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead>Invoice #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Balance Due</TableHead>
                <TableHead className="text-right">Running Balance</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.entries?.length > 0 ? data.entries.map((entry, i) => (
                <TableRow key={entry.invoice_id || i}>
                  <TableCell className="font-medium">#{entry.invoice_no}</TableCell>
                  <TableCell className="text-sm">{entry.date}</TableCell>
                  <TableCell className="text-sm">{entry.due_date}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{formatCurrency(entry.total)}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-emerald-600">{formatCurrency(entry.paid)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{formatCurrency(entry.balance_due)}</TableCell>
                  <TableCell className="text-right font-mono text-sm font-medium">{formatCurrency(entry.running_balance)}</TableCell>
                  <TableCell>
                    <Badge className={cn("text-[10px] capitalize",
                      entry.status === 'paid' && "bg-emerald-100 text-emerald-700",
                      entry.status === 'posted' && "bg-blue-100 text-blue-700",
                      entry.status === 'overdue' && "bg-red-100 text-red-700",
                    )}>
                      {entry.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <ScrollText className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50" />
                    <p className="text-muted-foreground">No statement entries yet</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
